/**
 * Модуль для загрузки файлов в Supabase Storage через TUS протокол
 * @module tusFileUploader
 *
 * @description
 * TUS (The Upload Server) - протокол для возобновляемых загрузок.
 * Преимущества TUS:
 * - Chunked upload (загрузка по частям)
 * - Возобновление после обрыва соединения
 * - Стабильная скорость при больших файлах
 *
 * Требования к серверу (self-hosted Supabase):
 * - TUS_URL_PATH: '/storage/v1/upload/resumable'
 * - REQUEST_ALLOW_X_FORWARDED_PATH: 'true'
 * - KONG_PORT_MAPS: "443:8000"
 */

import * as tus from "tus-js-client";
import { supabase } from "@/integrations/supabase/client";

/**
 * Ответ после успешной загрузки файла
 */
interface UploadResponse {
    url: string;
    key: string;
}

/**
 * Конфигурация для TUS загрузчика
 */
interface TusUploaderOptions {
    supabaseUrl: string;
    supabaseKey: string;
    supabaseBucket: string;
    /** ID текущего пользователя — резюм не должен пересекаться между аккаунтами */
    userId: string;
    /**
     * Размер chunk в байтах.
     * По умолчанию 50MB для ускорения загрузки.
     * Если сеть нестабильна, можно уменьшить до 6MB.
     */
    chunkSize?: number;
}

/**
 * Размер chunk по умолчанию: 6MB.
 * ВАЖНО: Supabase Storage (TUS) требует ровно 6MB — все чанки, кроме последнего,
 * должны быть 6MB, иначе загрузка рвётся на нестабильных/медленных каналах (VPN, мобильный).
 * Не увеличивать. См. https://supabase.com/docs/guides/storage/uploads/resumable-uploads
 */
const DEFAULT_CHUNK_SIZE = 6 * 1024 * 1024;

/**
 * Возвращает расширение ключа объекта в нижнем регистре либо пустую строку.
 * Точка в начале имени расширением не считается (`.gitkeep`).
 */
const getKeyExtension = (key: string): string => {
    const dotIndex = key.lastIndexOf(".");

    if (dotIndex <= 0 || dotIndex === key.length - 1) {
        return "";
    }

    return key.slice(dotIndex + 1).toLowerCase();
};

/**
 * Выбирает ключ объекта для возобновляемой загрузки.
 *
 * `tus-js-client` при резюме подхватывает `objectName` прошлой попытки: байты
 * уходят в старый ключ. Поэтому запись обязана ссылаться на унаследованный ключ,
 * а не на свежесгенерированный. Значение приходит из `localStorage` — это
 * недоверенный ввод, поэтому валидируется строго, и при любом сомнении
 * возвращается `freshKey` (честная новая загрузка вместо порчи чужого объекта).
 *
 * @param previousUpload - Найденная запись предыдущей загрузки (может отсутствовать)
 * @param freshKey - Ключ, сгенерированный для текущей попытки
 */
export const resolveResumedObjectKey = (
    previousUpload: { metadata?: Record<string, unknown> } | null | undefined,
    freshKey: string
): string => {
    const inherited = previousUpload?.metadata?.objectName;

    if (typeof inherited !== "string") {
        return freshKey;
    }

    const trimmed = inherited.trim();

    // Пустое имя или разделитель пути — попытка выйти за пределы собственного ключа
    if (trimmed.length === 0 || trimmed !== inherited || trimmed.includes("/") || trimmed.includes("\\")) {
        return freshKey;
    }

    if (getKeyExtension(trimmed) !== getKeyExtension(freshKey)) {
        return freshKey;
    }

    return trimmed;
};

/** Сообщение об отмене — на него завязана ветка обработки в вызывающем коде */
const UPLOAD_CANCELLED_MESSAGE = "Загрузка отменена";

/** Запись возобновления из `localStorage`. Все поля недоверенные. */
interface PreviousUploadRecord {
    uploadUrl?: unknown;
    size?: unknown;
    creationTime?: unknown;
    metadata?: Record<string, unknown>;
}

/**
 * Проверяет, можно ли возобновлять загрузку по этой записи.
 *
 * Отбраковывается всё, что может увести байты не туда: чужой размер, ссылка на
 * другой хост или другой путь, неразбираемое время создания и ключ, не
 * прошедший валидацию `resolveResumedObjectKey`.
 */
const isUsableResumeCandidate = (
    candidate: PreviousUploadRecord | null | undefined,
    { fileSize, endpoint, freshKey }: { fileSize: number; endpoint: string; freshKey: string }
): boolean => {
    if (!candidate) {
        return false;
    }

    // Ключ обязан быть унаследованным: без него запись указывала бы на объект,
    // в который загрузка не пойдёт
    if (resolveResumedObjectKey(candidate, freshKey) === freshKey) {
        return false;
    }

    if (typeof candidate.size !== "number" || candidate.size !== fileSize) {
        return false;
    }

    if (typeof candidate.creationTime !== "string" || !Number.isFinite(Date.parse(candidate.creationTime))) {
        return false;
    }

    if (typeof candidate.uploadUrl !== "string" || candidate.uploadUrl.trim().length === 0) {
        return false;
    }

    try {
        const uploadUrl = new URL(candidate.uploadUrl);
        const endpointUrl = new URL(endpoint);
        const endpointPrefix = endpointUrl.pathname.endsWith("/")
            ? endpointUrl.pathname
            : `${endpointUrl.pathname}/`;

        return uploadUrl.origin === endpointUrl.origin && uploadUrl.pathname.startsWith(endpointPrefix);
    } catch {
        return false;
    }
};

/**
 * Выбирает новейшего кандидата. При равном `creationTime` сохраняется исходный
 * порядок — иначе выбор становился бы недетерминированным.
 */
const pickLatestCandidate = (candidates: PreviousUploadRecord[]): PreviousUploadRecord | null =>
    candidates.reduce<PreviousUploadRecord | null>((best, candidate) => {
        if (!best) {
            return candidate;
        }

        return Date.parse(candidate.creationTime as string) > Date.parse(best.creationTime as string)
            ? candidate
            : best;
    }, null);

/**
 * Версия схемы fingerprint. Инкремент делает все ранее сохранённые записи
 * `localStorage` ненаходимыми — это осознанный разовый перезапуск загрузок.
 */
const FINGERPRINT_VERSION = "v2";

/**
 * Строит fingerprint загрузки, привязанный к пользователю и bucket.
 *
 * Стандартный fingerprint `tus-js-client` учитывает только сам файл и endpoint,
 * поэтому на общем устройстве один аккаунт подхватывает незавершённую загрузку
 * другого. Базовый алгоритм не копируется, а переиспользуется — так реализация
 * не разъезжается с библиотекой при её обновлении.
 *
 * Legacy-записи со старым unscoped fingerprint новым значением не находятся:
 * безопасно установить их владельца невозможно, поэтому они не мигрируются
 * и не удаляются автоматически.
 */
export const createScopedFingerprint =
    ({ supabaseBucket, userId }: { supabaseBucket: string; userId: string }) =>
    async (file: File, options: tus.UploadOptions): Promise<string | null> => {
        const baseFingerprint = await tus.defaultOptions.fingerprint(file, options);

        if (!baseFingerprint) {
            return null;
        }

        // JSON-сериализация массива однозначна: разделитель не может встретиться
        // внутри значения незаэкранированным
        return `${FINGERPRINT_VERSION}|${JSON.stringify([baseFingerprint, supabaseBucket, userId])}`;
    };

/**
 * Получает актуальный JWT токен сессии для авторизации
 */
async function getSessionToken(): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
        throw new Error("Необходима авторизация для загрузки файлов");
    }

    return session.access_token;
}

/**
 * Сколько раз повторять запрос, упавший из-за авторизации.
 * Повтор осмыслен потому, что onBeforeRequest поставит свежий токен.
 */
export const AUTH_RETRY_LIMIT = 3;

/**
 * Отказ авторизации на пути загрузки.
 *
 * Форма ответа снята живой пробой на проде 2026-09-10: протокольный статус 400,
 * а тело называет 403 —
 * {"statusCode":"403","code":"AccessDenied","error":"Unauthorized","message":"jwt expired"}
 * Поэтому проверяем и код, и тело.
 *
 * 🔴 Строку InvalidJWT здесь не ищем: она приходит с другого маршрута — со скачивания
 * документа по подписанной ссылке, а не с загрузки.
 */
export const isAuthFailureResponse = (status: number, body: string): boolean => {
    if (status === 401 || status === 403) return true;
    return status === 400 && /AccessDenied|Unauthorized/i.test(body);
};

/**
 * Решение о повторе запроса TUS.
 *
 * 🔴 Задав onShouldRetry, мы полностью замещаем поведение библиотеки, поэтому её
 * умолчание приходится воспроизводить здесь дословно. Оригинал (tus-js-client 4.3.1,
 * upload.js::defaultOnShouldRetry):
 *   (!inStatusCategory(status, 400) || status === 409 || status === 423) && isOnline()
 * Обратите внимание на 409: это конфликт смещения, библиотека повторяет его намеренно,
 * заново разрешая offset. Потерять его — значит сломать возобновление загрузки.
 */
export const shouldRetryTusError = (
    status: number,
    body: string,
    retryAttempt: number,
    isOnline: boolean,
): boolean => {
    if (!isOnline) return false;

    if (isAuthFailureResponse(status, body)) {
        return retryAttempt < AUTH_RETRY_LIMIT;
    }

    const isClientError = status >= 400 && status < 500;
    return !isClientError || status === 409 || status === 423;
};

/**
 * Создает TUS-based загрузчик файлов для Supabase Storage
 */
export const createTusFileUploader = ({
    supabaseUrl,
    supabaseKey,
    supabaseBucket,
    userId,
    chunkSize = DEFAULT_CHUNK_SIZE,
}: TusUploaderOptions) => {
    const fingerprint = createScopedFingerprint({ supabaseBucket, userId });

    /**
     * Загружает файл через TUS протокол
     * @param file - Файл для загрузки
     * @param onProgress - Callback для отслеживания прогресса (0-100)
     * @param abortSignal - Сигнал для отмены загрузки
     */
    const uploadFile = async (
        file: File,
        onProgress: (progress: number) => void,
        abortSignal?: AbortSignal
    ): Promise<UploadResponse> => {
        const fileExt = file.name.split(".").pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = fileName;

        // Ранняя проверка авторизации: если сессии нет, падаем до создания загрузки,
        // а не на первом запросе. Сам токен в заголовок отсюда не идёт — его на каждый
        // запрос ставит onBeforeRequest.
        await getSessionToken();

        // TUS endpoint для Supabase Storage
        const tusEndpoint = `${supabaseUrl}/storage/v1/upload/resumable`;

        return new Promise((resolve, reject) => {
            // Проверяем, не отменена ли загрузка до начала
            if (abortSignal?.aborted) {
                reject(new Error(UPLOAD_CANCELLED_MESSAGE));
                return;
            }

            onProgress(0);

            // Фактический ключ объекта. При возобновлении заменяется на
            // унаследованный: байты уходят туда, куда их уже начал писать tus.
            let resolvedKey = filePath;
            let settled = false;

            const detachAbortListener = () => abortSignal?.removeEventListener("abort", handleAbort);

            const settleSuccess = (response: UploadResponse) => {
                if (settled) return;
                settled = true;
                detachAbortListener();
                resolve(response);
            };

            const settleError = (error: Error) => {
                if (settled) return;
                settled = true;
                detachAbortListener();
                reject(error);
            };

            // Последний токен, ушедший в заголовке. Нужен только для того, чтобы отличить
            // смену токена от обычного запроса и не засорять консоль. В логи не попадает.
            let lastUsedAccessToken: string | null = null;

            function handleAbort() {
                if (settled) return;

                // true = удалить частично загруженный файл. Промис может
                // отклониться (сеть, уже удалённый объект) — это не должно
                // всплывать как unhandled rejection и не меняет исход отмены.
                Promise.resolve(upload.abort(true)).catch((error) => {
                    console.error("TUS abort error:", error);
                });

                settleError(new Error(UPLOAD_CANCELLED_MESSAGE));
            }

            const upload = new tus.Upload(file, {
                endpoint: tusEndpoint,
                // Пятая задержка добавлена под повторы авторизации (AUTH_RETRY_LIMIT = 3),
                // чтобы у них оставался запас поверх обычных сетевых повторов.
                retryDelays: [0, 1000, 3000, 5000, 10000],
                chunkSize: chunkSize,

                // 🔴 `authorization` здесь НЕТ намеренно — его ставит только onBeforeRequest.
                // Браузерный стек библиотеки реализует setHeader как
                // `xhr.setRequestHeader(header, value)`, а тот по спецификации XHR
                // **дописывает** значение к уже существующему через запятую, а не заменяет
                // его. Если оставить токен и здесь, и в хуке, уходит
                // `authorization: Bearer <jwt>, Bearer <jwt>` и Storage отвечает
                // 400 «jwt malformed». Поймано живой приёмкой на стенде 2026-09-10.
                headers: {
                    apikey: supabaseKey,                    // Anon Key (для доступа к API gateway)
                    "x-upsert": "true",
                },

                // Токен живёт час (JWT_EXPIRY=3600), а загрузка большого файла идёт дольше.
                // Статический заголовок выше протухает на середине, и Storage отвечает
                // HTTP 400 с телом {"code":"AccessDenied","error":"Unauthorized",
                // "message":"jwt expired"} — загрузка обрывалась безвозвратно.
                // Хук вызывается перед КАЖДЫМ запросом, включая повторы, поэтому заголовок
                // всегда несёт актуальный токен.
                // Обновлять сессию вручную здесь нельзя: getSession() сам обновляет токен
                // за EXPIRY_MARGIN_MS (90 с) до истечения и делает это под внутренней
                // блокировкой. Явный refreshSession() прошёл бы мимо неё и мог провернуть
                // ротацию одновременно с фоновым таймером, разлогинив пользователя.
                onBeforeRequest: async (req) => {
                    let token: string;
                    try {
                        token = await getSessionToken();
                    } catch (error) {
                        // Сессии нет — дальше запрос смысла не имеет, пусть падает явно.
                        console.error("[tus] no session for upload", error);
                        throw error;
                    }

                    // Логируем ТОЛЬКО факт смены токена: на каждый кусок писать нечего,
                    // а смена — это и есть доказательство, что фикс сработал.
                    // 🔴 Ни сам токен, ни его фрагмент в лог не попадают.
                    if (lastUsedAccessToken !== null && lastUsedAccessToken !== token) {
                        console.info("[tus] access token changed mid-upload, header refreshed");
                    }
                    lastUsedAccessToken = token;

                    req.setHeader("authorization", `Bearer ${token}`);
                },

                onShouldRetry: (error, retryAttempt) => {
                    const response = error.originalResponse;
                    const status = response ? response.getStatus() : 0;
                    const body = response ? response.getBody() : "";
                    const isOnline =
                        typeof navigator === "undefined" || navigator.onLine !== false;

                    const retry = shouldRetryTusError(status, body, retryAttempt, isOnline);

                    if (isAuthFailureResponse(status, body)) {
                        // Тело целиком не печатаем — только код и решение.
                        console.warn(
                            "[tus] retry after auth failure, attempt=",
                            retryAttempt,
                            "status=",
                            status,
                            "retry=",
                            retry,
                        );
                    }

                    return retry;
                },

                uploadDataDuringCreation: true,
                removeFingerprintOnSuccess: true,
                fingerprint,

                metadata: {
                    bucketName: supabaseBucket,
                    objectName: filePath,
                    contentType: file.type,
                    cacheControl: "3600",
                },

                onError: (error) => {
                    console.error("TUS upload error:", error);
                    if ('originalRequest' in error && (error as unknown as { originalRequest?: { getURL(): string } }).originalRequest) {
                        const req = (error as unknown as { originalRequest: { getURL(): string } }).originalRequest;
                        console.error("Failed request URL:", req.getURL());
                    }
                    settleError(new Error(`Ошибка загрузки: ${error.message}`));
                },

                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = (bytesUploaded / bytesTotal) * 100;
                    onProgress(percentage);
                },

                onSuccess: () => {
                    // Публичный URL строится по фактическому ключу объекта
                    const {
                        data: { publicUrl },
                    } = supabase.storage.from(supabaseBucket).getPublicUrl(resolvedKey);

                    onProgress(100);

                    settleSuccess({
                        url: publicUrl,
                        key: resolvedKey,
                    });
                },
            });

            abortSignal?.addEventListener("abort", handleAbort, { once: true });

            /** Стартует загрузку, если её не отменили, пока шёл поиск */
            const startUnlessAborted = () => {
                if (settled || abortSignal?.aborted) {
                    return;
                }

                upload.start();
            };

            // Ищем незавершённые загрузки этого же файла и наследуем их ключ
            upload
                .findPreviousUploads()
                .then((previousUploads) => {
                    const candidates = (previousUploads as PreviousUploadRecord[]).filter((candidate) =>
                        isUsableResumeCandidate(candidate, {
                            fileSize: file.size,
                            endpoint: tusEndpoint,
                            freshKey: filePath,
                        })
                    );

                    const candidate = pickLatestCandidate(candidates);

                    if (candidate) {
                        resolvedKey = resolveResumedObjectKey(candidate, filePath);

                        // Мутация до resume/start обязательна: ветка рестарта
                        // после HEAD 400 перечитывает options.metadata заново
                        upload.options.metadata.objectName = resolvedKey;
                        upload.resumeFromPreviousUpload(
                            candidate as unknown as tus.PreviousUpload
                        );

                        console.log("TUS resume: inherited object key", resolvedKey);
                    }

                    startUnlessAborted();
                })
                .catch((error) => {
                    // Поиск в localStorage не критичен: начинаем загрузку заново
                    console.error("TUS findPreviousUploads error:", error);
                    startUnlessAborted();
                });
        });
    };

    return { uploadFile };
};

/**
 * Фабричная функция для обратной совместимости
 */
export const createFileUploader = createTusFileUploader;
